import { Directive, ElementRef, NgZone, OnDestroy, OnInit, Renderer2 } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { fromEvent, Subject } from "rxjs";
import { auditTime, takeUntil } from "rxjs/operators";
import { ViewportService } from "../../core/viewport/viewport.service";

/** Margen (px) para considerar que el scroll llego a un extremo. */
const EDGE_TOLERANCE = 2;

/** Ancho aproximado de un submenu, para que no se salga por la derecha. */
const SUBMENU_WIDTH = 200;

/**
 * Menu de pestañas ".nav-root" con scroll horizontal en layout compacto.
 *
 * Se aplica sola a todo elemento con la clase "nav-root" dentro de modulos que
 * importan ComponentsModule (no hay que tocar los templates). En < 1400px el SCSS
 * (src/app/shared/styles/app/nav-root.scss) pone las pestañas en una sola fila
 * con scroll; esta directiva:
 *   - Agrega dos flechas "sticky" encimadas sobre las pestañas: [ < ] al inicio
 *     y [ > ] al final. Cada una mueve el scroll una pestaña hacia su lado.
 *   - Marca el host con "nav-has-prev" / "nav-has-next" segun queden pestañas
 *     ocultas a cada lado; el SCSS usa esas clases para mostrar u ocultar cada
 *     flecha con una transicion (sin cambiar el layout, no hay saltos).
 *   - Submenus en compacto: se abren y cierran con un toque (clase
 *     "nav-submenu-open" en la pestaña; :hover no es confiable en tactil) y se
 *     posicionan debajo de su pestaña (son position: fixed, porque el
 *     contenedor con scroll recortaria un absolute). Se cierran al elegir una
 *     opcion, al tocar fuera o al mover el scroll del menu.
 * En escritorio las flechas estan ocultas por CSS y el menu se ve igual que antes.
 * Todo corre fuera de la zona de Angular: no dispara deteccion de cambios.
 */
@Directive({
  selector: ".nav-root"
})
export class NavScrollDirective implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private prevButton: HTMLButtonElement;
  private nextButton: HTMLButtonElement;
  private resizeObserver: any;
  private mutationObserver: MutationObserver;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private ngZone: NgZone,
    private translate: TranslateService,
    private viewport: ViewportService
  ) {}

  /**
   * Crea las flechas y registra los listeners de scroll, tamaño y submenus.
   * @returns void
   */
  ngOnInit(): void {
    const host = this.el.nativeElement;
    this.ngZone.runOutsideAngular(() => {
      this.prevButton = this.createArrow("nav-scroll-prev", "fa-chevron-left", () =>
        this.scrollByTab(-1)
      );
      this.nextButton = this.createArrow("nav-scroll-next", "fa-chevron-right", () =>
        this.scrollByTab(1)
      );

      fromEvent(host, "scroll", { passive: true } as any)
        .pipe(auditTime(50), takeUntil(this.destroy$))
        .subscribe(() => {
          this.updateArrows();
          this.closeSubmenus();
        });

      fromEvent<MouseEvent>(host, "click")
        .pipe(takeUntil(this.destroy$))
        .subscribe((event) => this.toggleSubmenu(event.target as HTMLElement));

      fromEvent<MouseEvent>(document, "click")
        .pipe(takeUntil(this.destroy$))
        .subscribe((event) => {
          if (!host.contains(event.target as Node)) {
            this.closeSubmenus();
          }
        });

      fromEvent(window, "resize")
        .pipe(auditTime(100), takeUntil(this.destroy$))
        .subscribe(() => {
          this.updateArrows();
          this.closeSubmenus();
        });

      fromEvent<MouseEvent>(host, "mouseover")
        .pipe(takeUntil(this.destroy$))
        .subscribe((event) => this.placeSubmenu(event.target as HTMLElement));

      fromEvent<TouchEvent>(host, "touchstart", { passive: true } as any)
        .pipe(takeUntil(this.destroy$))
        .subscribe((event: TouchEvent) => this.placeSubmenu(event.target as HTMLElement));

      // Las pestañas pueden llegar despues (ngFor, *ngIf, traducciones)
      const ResizeObserverCtor = (window as any).ResizeObserver;
      if (ResizeObserverCtor) {
        this.resizeObserver = new ResizeObserverCtor(() => this.updateArrows());
        this.resizeObserver.observe(host);
      }
      this.mutationObserver = new MutationObserver(() => this.updateArrows());
      this.mutationObserver.observe(host, { childList: true, subtree: true, characterData: true });

      this.updateArrows();
    });

    this.translate
      .stream(["general.previous-tab", "general.next-tab"])
      .pipe(takeUntil(this.destroy$))
      .subscribe((labels: { [key: string]: string }) => {
        this.setLabel(this.prevButton, labels["general.previous-tab"]);
        this.setLabel(this.nextButton, labels["general.next-tab"]);
      });
  }

  /**
   * Libera observers y listeners.
   * @returns void
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  // Crea una flecha con su icono y su accion de click
  private createArrow(className: string, icon: string, onClick: () => void): HTMLButtonElement {
    const button: HTMLButtonElement = this.renderer.createElement("button");
    this.renderer.setAttribute(button, "type", "button");
    this.renderer.setAttribute(button, "tabindex", "-1");
    this.renderer.addClass(button, "nav-scroll-arrow");
    this.renderer.addClass(button, className);
    const iconEl = this.renderer.createElement("i");
    this.renderer.addClass(iconEl, "fa");
    this.renderer.addClass(iconEl, icon);
    this.renderer.setAttribute(iconEl, "aria-hidden", "true");
    this.renderer.appendChild(button, iconEl);
    // Sin foco al tocar: enfocar un elemento sticky hace que el navegador mueva el scroll
    this.renderer.listen(button, "mousedown", (event: Event) => event.preventDefault());
    this.renderer.listen(button, "click", (event: Event) => {
      event.stopPropagation();
      onClick();
    });
    return button;
  }

  // Texto accesible de una flecha
  private setLabel(button: HTMLButtonElement, label: string): void {
    this.renderer.setAttribute(button, "aria-label", label);
    this.renderer.setAttribute(button, "title", label);
  }

  // Mantiene las flechas en los extremos y actualiza que lado tiene pestañas ocultas
  private updateArrows(): void {
    const host = this.el.nativeElement;
    // Si Angular agrega pestañas, se vuelven a acomodar las flechas en los extremos
    if (host.firstElementChild !== this.prevButton) {
      host.insertBefore(this.prevButton, host.firstElementChild);
    }
    if (host.lastElementChild !== this.nextButton) {
      host.appendChild(this.nextButton);
    }
    const hasPrev = host.scrollLeft > EDGE_TOLERANCE;
    const hasNext = host.scrollLeft + host.clientWidth < host.scrollWidth - EDGE_TOLERANCE;
    this.toggleClass(host, "nav-has-prev", hasPrev);
    this.toggleClass(host, "nav-has-next", hasNext);
  }

  // Agrega o quita una clase segun la condicion
  private toggleClass(element: HTMLElement, className: string, active: boolean): void {
    if (active) {
      element.classList.add(className);
      return;
    }
    element.classList.remove(className);
  }

  /*
    Mueve el scroll una pestaña: hacia adelante deja al inicio la primera
    pestaña cortada a la derecha; hacia atras, la ultima cortada a la
    izquierda. Se descuenta el ancho de la flecha porque queda encima.
  */
  private scrollByTab(direction: 1 | -1): void {
    const host = this.el.nativeElement;
    // La flecha de un lado sin pestañas ocultas no hace nada (esta oculta)
    const sideClass = direction > 0 ? "nav-has-next" : "nav-has-prev";
    if (!host.classList.contains(sideClass)) {
      return;
    }
    const hostRect = host.getBoundingClientRect();
    const arrowWidth = this.nextButton.offsetWidth;
    // Solo pestañas visibles (algunas se ocultan con d-none por permisos)
    const items = (Array.from(host.children) as HTMLElement[]).filter(
      (child) => child !== this.prevButton && child !== this.nextButton && child.offsetWidth > 0
    );

    const maxScroll = host.scrollWidth - host.clientWidth;
    // Candidatas: pestañas cortadas del lado hacia el que se mueve (en orden de cercania)
    const candidates =
      direction > 0
        ? items.filter(
            (item) =>
              item.getBoundingClientRect().right > hostRect.right - arrowWidth + EDGE_TOLERANCE
          )
        : items
            .filter(
              (item) =>
                item.getBoundingClientRect().left < hostRect.left + arrowWidth - EDGE_TOLERANCE
            )
            .reverse();
    if (candidates.length === 0) {
      return;
    }

    // Se descarta un paso menor al ancho de la flecha (se sentiria como que no avanza)
    let left = host.scrollLeft;
    for (const item of candidates) {
      left = host.scrollLeft + item.getBoundingClientRect().left - hostRect.left - arrowWidth;
      if (Math.abs(left - host.scrollLeft) >= arrowWidth) {
        break;
      }
    }

    // Si falta menos que una flecha para el extremo de ese lado, se llega directo
    if (direction < 0 && left <= arrowWidth) {
      left = 0;
    }
    if (direction > 0 && maxScroll - left <= arrowWidth) {
      left = maxScroll;
    }
    host.scrollTo({ left, behavior: "smooth" });
  }

  // Toque en una pestaña con submenu: lo abre o cierra; elegir una opcion lo cierra
  private toggleSubmenu(target: HTMLElement): void {
    if (!this.viewport.isCompact || !target) {
      return;
    }
    if (target.closest(".submenu")) {
      this.closeSubmenus();
      return;
    }
    const item = target.closest(".nav-item") as HTMLElement;
    if (!item || !item.querySelector(".submenu")) {
      this.closeSubmenus();
      return;
    }
    const wasOpen = item.classList.contains("nav-submenu-open");
    this.closeSubmenus();
    if (!wasOpen) {
      this.placeSubmenu(item);
      item.classList.add("nav-submenu-open");
    }
  }

  // Cierra los submenus abiertos y limpia la posicion calculada para compacto
  private closeSubmenus(): void {
    const host = this.el.nativeElement;
    const open = host.querySelectorAll(".nav-submenu-open");
    Array.from(open).forEach((item) => item.classList.remove("nav-submenu-open"));
    const submenus = host.querySelectorAll(".submenu") as NodeListOf<HTMLElement>;
    Array.from(submenus).forEach((submenu) => {
      submenu.style.top = "";
      submenu.style.left = "";
    });
  }

  // Coloca el submenu (position: fixed en compacto) debajo de su pestaña
  private placeSubmenu(target: HTMLElement): void {
    const item = target && (target.closest(".nav-item") as HTMLElement);
    const submenu = item && (item.querySelector(".submenu") as HTMLElement);
    if (!submenu) {
      return;
    }
    // En escritorio el submenu es absolute: se limpia cualquier posicion previa
    if (!this.viewport.isCompact) {
      submenu.style.top = "";
      submenu.style.left = "";
      return;
    }
    const rect = item.getBoundingClientRect();
    const left = Math.max(0, Math.min(rect.left, window.innerWidth - SUBMENU_WIDTH));
    submenu.style.top = `${rect.bottom}px`;
    submenu.style.left = `${left}px`;
  }
}
